import {clsx} from 'clsx';
import {Avatar as RadixAvatarNS} from 'radix-ui';
import type {ComponentProps, ElementRef} from 'react';
import {forwardRef, memo} from 'react';

import type {Color, Size} from '#theme';
import type {WithoutClassName} from '#types';
import type {CommonUIAttributes} from '#types/ui.js';

import type {EXCLUDED_AVATAR_COLORS} from './constants.js';
import {DEFAULT_COLOR, DEFAULT_NAME, DEFAULT_SIZE} from './constants.js';
import * as styles from './style.module.scss';
import type {AvatarStyle} from './types.js';
import {useAvatarStyles} from './utils.js';
const RadixAvatarPrimitive = RadixAvatarNS.Root;
const RadixAvatarImage = RadixAvatarNS.Image;
const RadixAvatarFallback = RadixAvatarNS.Fallback;

type CustomAvatarRootProps = {
  name?: string;
  size?: Size;
  color?: Exclude<Color, typeof EXCLUDED_AVATAR_COLORS>;
  randomize?: boolean;
  style?: AvatarStyle;
  className?: string;
};

export type AvatarRootProps = WithoutClassName<
  ComponentProps<typeof RadixAvatarPrimitive>
> &
  CommonUIAttributes &
  CustomAvatarRootProps;

const Root = memo(
  forwardRef<ElementRef<typeof RadixAvatarPrimitive>, AvatarRootProps>(
    (
      {
        name = DEFAULT_NAME,
        size = DEFAULT_SIZE,
        color = DEFAULT_COLOR,
        randomize,
        style,
        className,
        children,
        ...rest
      },
      ref
    ) => {
      const rootStyles = useAvatarStyles({
        name,
        size,
        color,
        randomize,
        style,
      });
      return (
        <RadixAvatarPrimitive
          ref={ref}
          className={clsx(styles.avatar, className, [
            children && styles.avatarWithChildren,
          ])}
          style={rootStyles}
          {...rest}
        >
          {children ? (
            children
          ) : (
            <Fallback>{name?.toUpperCase().substring(0, 1)}</Fallback>
          )}
        </RadixAvatarPrimitive>
      );
    }
  )
);

export type AvatarImageProps = WithoutClassName<
  ComponentProps<typeof RadixAvatarImage>
>;

const Image = memo(
  forwardRef<ElementRef<typeof RadixAvatarImage>, Partial<AvatarImageProps>>(
    (props, ref) => {
      return (
        <RadixAvatarImage ref={ref} {...props} className={styles.avatarImage} />
      );
    }
  )
);

export type AvatarFallbackProps = WithoutClassName<
  ComponentProps<typeof RadixAvatarFallback>
>;

const Fallback = memo(
  forwardRef<
    ElementRef<typeof RadixAvatarFallback>,
    Partial<AvatarFallbackProps>
  >((props, ref) => {
    return (
      <RadixAvatarFallback
        ref={ref}
        className={styles.avatarFallback}
        {...props}
      />
    );
  })
);

Root.displayName = 'Avatar';
Image.displayName = 'Avatar.Image';
Fallback.displayName = 'Avatar.Fallback';

export const Avatar = Object.assign(Root, {
  Image,
  Fallback,
});
